import { getPendingZoneGroups } from 'features/evacZones/components/PendingEvacZonesWarning/PendingEvacZonesWarning.utils';
import { PendingReviewEvacZoneErrorType } from 'features/wildfires/wildfire.types';
import { TFunction } from 'i18next';
import {
  EvacZone,
  FormEvacZoneStatus,
  GeoEventEvacZoneStatus,
} from 'shared/types';
import { formatNameList } from 'shared/utils';
import * as Yup from 'yup';

type EvacZoneStatus = GeoEventEvacZoneStatus | FormEvacZoneStatus;

export const PENDING_REVIEW_ZONES_ERRORS: Record<
  string,
  PendingReviewEvacZoneErrorType
> = {
  notAccepted: 'not-accepted',
  restrictedWithStatus: 'restricted-with-status',
};

const getAcceptedPendingZoneUids = (
  regionEvacZones: EvacZone[],
  previousEvacZoneUids: string[],
  releasePendingZoneSet: Set<string>,
): string[] => {
  const pendingZoneGroups = getPendingZoneGroups(
    regionEvacZones,
    previousEvacZoneUids,
  );

  return pendingZoneGroups
    .filter((group) =>
      group.releaseUids.some((uid) => releasePendingZoneSet.has(uid)),
    )
    .flatMap((group) => group.zones.map((zone) => zone.uidV2));
};

const getChangedOrRemovedPendingZones = (
  previousStatuses: GeoEventEvacZoneStatus[],
  currentStatusMap: Map<string, EvacZoneStatus>,
  pendingZoneSet: Set<string>,
  releasePendingZoneSet: Set<string>,
): EvacZoneStatus[] =>
  previousStatuses.filter((prevZone) => {
    const isPending = pendingZoneSet.has(prevZone.evacZone.uidV2);
    const isReleased = releasePendingZoneSet.has(prevZone.evacZone.uidV2);
    if (!isPending || isReleased) return false;

    const currentZone = currentStatusMap.get(prevZone.evacZone.uidV2);
    const isRemoved = !currentZone;
    const isChanged = currentZone && prevZone.status !== currentZone.status;

    return isRemoved || isChanged;
  });

const getAddedPendingZones = (
  regionEvacZones: EvacZone[],
  previousStatuses: GeoEventEvacZoneStatus[],
  currentStatuses: EvacZoneStatus[],
  pendingZoneSet: Set<string>,
  releasePendingZoneSet: Set<string>,
): EvacZoneStatus[] => {
  const previousStatusUids = new Set(
    previousStatuses.map((status) => status.evacZone.uidV2),
  );

  const acceptedReleaseUids = new Set(
    getAcceptedPendingZoneUids(
      regionEvacZones,
      [...previousStatusUids],
      releasePendingZoneSet,
    ),
  );

  return currentStatuses.filter((currentZone) => {
    const { uidV2 } = currentZone.evacZone;
    const isNew = !previousStatusUids.has(uidV2);
    const isPending = pendingZoneSet.has(uidV2);
    const isAccepted = acceptedReleaseUids.has(uidV2);

    return isNew && isPending && !isAccepted;
  });
};

/**
 * Identifies zones with restricted statuses after a group's pending review is accepted.
 * These are typically parent/active zones in a split/merge that are being replaced
 * and should not retain a status.
 */
const getRestrictedStatusZones = (
  currentStatuses: EvacZoneStatus[],
  pendingZoneMap: Map<string, EvacZone>,
  releasePendingZoneSet: Set<string>,
  previousStatusMap: Map<string, GeoEventEvacZoneStatus>,
): EvacZoneStatus[] =>
  currentStatuses.filter((zone) => {
    const { uidV2 } = zone.evacZone;
    const pendingZone = pendingZoneMap.get(uidV2);
    if (!pendingZone?.isActive) return false;

    const hasReleaseBeenAccepted = releasePendingZoneSet.has(uidV2);
    const previousStatus = previousStatusMap.get(uidV2);

    // If status is unchanged and release isn't accepted, it's not restricted
    if (previousStatus?.status === zone.status && !hasReleaseBeenAccepted) {
      return false;
    }

    // A zone survives a split if it lists itself as both a parent and a child
    const willSurviveSplit =
      pendingZone.parentUids?.includes(uidV2) &&
      pendingZone.childUids?.includes(uidV2);

    return !willSurviveSplit;
  });

export const getEvacZoneStatusesValidationSchema = (
  t: TFunction,
  regionEvacZones: EvacZone[],
  geoEventEvacZoneStatuses: GeoEventEvacZoneStatus[],
): {
  evacZoneStatuses: Yup.AnySchema;
} => {
  const previousStatusMap = new Map(
    geoEventEvacZoneStatuses.map((status) => [status.evacZone.uidV2, status]),
  );

  const pendingReviewEvacZones = regionEvacZones.filter(
    (zone) => zone.isPendingReview,
  );

  const pendingZoneMap = new Map(
    pendingReviewEvacZones.map((zone) => [zone.uidV2, zone]),
  );

  const pendingZoneSet = new Set(
    pendingReviewEvacZones.map((zone) => zone.uidV2),
  );

  return {
    evacZoneStatuses: Yup.array().test(
      'pending-review-zones',
      '', // Placeholder message, handled by createError
      // eslint-disable-next-line func-names
      function (currentStatuses: EvacZoneStatus[] | undefined) {
        if (!currentStatuses) return true;

        const releasePendingZoneUids: string[] =
          this.parent?.releasePendingZoneUids || [];
        const releasePendingZoneSet = new Set(releasePendingZoneUids);
        const currentStatusMap = new Map(
          currentStatuses.map((status) => [status.evacZone.uidV2, status]),
        );

        // Check for pending zones that were changed/added without acceptance
        const changedOrRemovedZones = getChangedOrRemovedPendingZones(
          geoEventEvacZoneStatuses,
          currentStatusMap,
          pendingZoneSet,
          releasePendingZoneSet,
        );
        const addedZones = getAddedPendingZones(
          regionEvacZones,
          geoEventEvacZoneStatuses,
          currentStatuses,
          pendingZoneSet,
          releasePendingZoneSet,
        );
        const unacceptedPendingZones = [
          ...changedOrRemovedZones,
          ...addedZones,
        ];

        if (unacceptedPendingZones.length) {
          return this.createError({
            message: formatNameList(
              t,
              unacceptedPendingZones.map((zone) => zone.evacZone.displayName),
            ),
            path: this.path,
            type: PENDING_REVIEW_ZONES_ERRORS.notAccepted,
          });
        }

        // Check for accepted zones that are not allowed to have a status
        const restrictedStatusZones = getRestrictedStatusZones(
          currentStatuses,
          pendingZoneMap,
          releasePendingZoneSet,
          previousStatusMap,
        );

        if (restrictedStatusZones.length) {
          return this.createError({
            message: formatNameList(
              t,
              restrictedStatusZones.map((zone) => zone.evacZone.displayName),
            ),
            path: this.path,
            type: PENDING_REVIEW_ZONES_ERRORS.restrictedWithStatus,
          });
        }

        return true;
      },
    ),
  };
};
